/**
 * Schema Cache Metadata Bus
 *
 * Single renderer-side bus that exposes a normalized `SchemaSnapshot` per
 * `(connectionId, database)` to the IntelliSense orchestrator. The bus itself
 * is storage + pub/sub only — per-engine adapters (next slice) project each
 * engine's native cache shape into `SchemaSnapshot` and publish it here.
 *
 * Invalidation is driven by the existing DDL-detection path in
 * `useQueryExecution.ts`, not by a parallel watcher. Disconnect should call
 * `invalidateConnection(connectionId)` so subscribers can drop derived state.
 */

import type { SchemaSnapshot } from '@/core/engines/intellisenseTypes';

/**
 * Events delivered to subscribers. The orchestrator + adapters branch on
 * `type` to decide whether to recompute, drop derived state, or refresh.
 */
export type SchemaBusEvent =
  | { type: 'updated'; connectionId: string; database: string | undefined }
  | { type: 'invalidated'; connectionId: string; objectKind?: string }
  | { type: 'disconnected'; connectionId: string }
  | { type: 'warmRequested'; connectionId: string; database: string | undefined };

export type SchemaBusListener = (event: SchemaBusEvent) => void;

const snapshots: Map<string, SchemaSnapshot> = new Map();
const listeners: Map<string, Set<SchemaBusListener>> = new Map();

function snapshotKey(connectionId: string, database: string | undefined): string {
  return `${connectionId}|${database ?? ''}`;
}

function emit(connectionId: string, event: SchemaBusEvent): void {
  const set = listeners.get(connectionId);
  if (!set) return;
  // Iterate a snapshot of the listener set so a misbehaving listener that
  // unsubscribes (or another listener) mid-iteration cannot drop a delivery.
  for (const listener of Array.from(set)) {
    try {
      listener(event);
    } catch {
      // Swallow listener errors; a buggy subscriber must not stall the bus
      // for everyone else. We deliberately do not re-throw or log here —
      // the bus is process-agnostic and has no opinion on logging surfaces.
    }
  }
}

/**
 * Returns the current snapshot for `(connectionId, database)`, or `null` if
 * none has been published yet (cold cache). Synchronous; never blocks.
 */
export function getSnapshot(
  connectionId: string,
  database: string | undefined,
): SchemaSnapshot | null {
  return snapshots.get(snapshotKey(connectionId, database)) ?? null;
}

/**
 * Databases of `connectionId` that currently hold a published snapshot —
 * the set a connection-wide manual refresh has to re-read (a database never
 * loaded has nothing stale to replace).
 */
export function publishedDatabases(connectionId: string): string[] {
  const prefix = `${connectionId}|`;
  const databases: string[] = [];
  for (const key of snapshots.keys()) {
    if (!key.startsWith(prefix)) continue;
    const database = key.slice(prefix.length);
    if (database) databases.push(database);
  }
  return databases;
}

/**
 * Subscribes a listener to bus events for `connectionId`. Returns an
 * unsubscribe function.
 */
export function subscribe(connectionId: string, listener: SchemaBusListener): () => void {
  let set = listeners.get(connectionId);
  if (!set) {
    set = new Set();
    listeners.set(connectionId, set);
  }
  set.add(listener);
  return () => {
    const current = listeners.get(connectionId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(connectionId);
  };
}

/**
 * Marks every snapshot for `connectionId` as stale and notifies subscribers.
 * Called from the existing DDL detection path in `useQueryExecution.ts` —
 * no parallel watcher.
 *
 * `objectKind` is forwarded to subscribers verbatim (e.g. `'routine'`,
 * `'table'`, `'schema'`) so per-engine adapters can decide how aggressively
 * to refresh their underlying cache.
 */
export function invalidateSchema(connectionId: string, objectKind?: string): void {
  for (const key of Array.from(snapshots.keys())) {
    if (key.startsWith(`${connectionId}|`)) snapshots.delete(key);
  }
  emit(connectionId, { type: 'invalidated', connectionId, objectKind });
}

/**
 * Drops every snapshot for `connectionId` and removes all of its subscribers.
 * Called on disconnect / connection deletion.
 */
export function invalidateConnection(connectionId: string): void {
  for (const key of Array.from(snapshots.keys())) {
    if (key.startsWith(`${connectionId}|`)) snapshots.delete(key);
  }
  emit(connectionId, { type: 'disconnected', connectionId });
  listeners.delete(connectionId);
}

/**
 * Requests an eager warm for `(connectionId, database)`. Adapters that have
 * subscribed will react by introspecting and calling `publish`. The bus
 * itself does no work beyond fanning the event out.
 */
export function warmConnection(connectionId: string, database?: string): void {
  emit(connectionId, { type: 'warmRequested', connectionId, database });
}

/** `false` drops the snapshot instead of publishing it. */
export type SchemaBusPublishGuard = (snapshot: SchemaSnapshot) => boolean;

let publishGuard: SchemaBusPublishGuard = () => true;

/**
 * Installs the check every `publish` must pass. The production guard
 * (`connectionEngineGuard.ts`) drops a snapshot built by another engine's
 * loader than the connection's own: the key carries no engine, so a
 * wrong-engine load's empty degraded snapshot (SQL Server catalog queries
 * failing against MySQL) would replace the real engine's snapshot — or,
 * landing first, stop the real engine's resolver from ever warming, since it
 * only warms a missing one. A hook rather than an import because that guard
 * reads the connection store, and the store imports this bus.
 */
export function setPublishGuard(guard: SchemaBusPublishGuard): void {
  publishGuard = guard;
}

/**
 * Stores a snapshot under `(snapshot.connectionId, snapshot.database)` and
 * notifies subscribers with an `updated` event. Adapters call this from
 * their cache-refresh path; tests call it directly to seed fixtures.
 */
export function publish(snapshot: SchemaSnapshot): void {
  if (!publishGuard(snapshot)) return;
  snapshots.set(snapshotKey(snapshot.connectionId, snapshot.database), snapshot);
  emit(snapshot.connectionId, {
    type: 'updated',
    connectionId: snapshot.connectionId,
    database: snapshot.database,
  });
}

// E2E diagnostic peek — lets Playwright specs read the live bus state
// (snapshot presence, FK-edge content) when root-causing live-vs-unit rank
// divergences. Zero-cost in production: a single window assignment at module
// load; nothing reads it outside e2e diagnostics.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__jamSqlSchemaBusPeek__ = getSnapshot;
}

/**
 * Test seam — clears all bus state. Tests should call this in `afterEach`
 * to avoid leaking state across cases.
 */
export function __resetSchemaBusForTest(): void {
  snapshots.clear();
  listeners.clear();
}

/**
 * Test-only fixture seam — injects a `SchemaSnapshot` for `(connectionId,
 * database)` and notifies subscribers exactly the same way `publish` does.
 * Mirrors the existing `injectPgSchemaForTest` pattern from
 * `pgSchemaCache.ts:34`.
 *
 * Distinct from `publish` so a future iteration can add invariants only
 * relevant to fixtures (e.g. validation of FK edge consistency) without
 * affecting the production write path.
 *
 * Gated behind `process.env.SQL_STUDIO_TEST === '1'` (set by
 * `appFixture.ts:189` for E2E) OR `process.env.NODE_ENV === 'test'` (set
 * by Vitest by default). Outside both flags the helper throws so a misuse
 * from production code fails loudly.
 */
export function injectSnapshotForTest(
  connectionId: string,
  database: string | undefined,
  snapshot: SchemaSnapshot,
): void {
  if (typeof process !== 'undefined') {
    const isTest = process.env?.SQL_STUDIO_TEST === '1' || process.env?.NODE_ENV === 'test';
    if (!isTest) {
      throw new Error(
        'injectSnapshotForTest is only available in test mode (SQL_STUDIO_TEST=1 or NODE_ENV=test); refusing to mutate the bus from production code.',
      );
    }
  }
  // Coerce the snapshot's connectionId/database to the requested key so a
  // caller that builds a snapshot with a different shape can't accidentally
  // store it under the wrong slot.
  const coerced: SchemaSnapshot = {
    ...snapshot,
    connectionId,
    database,
  };
  snapshots.set(snapshotKey(connectionId, database), coerced);
  emit(connectionId, { type: 'updated', connectionId, database });
}
